import React, { useState, lazy } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, Phone, CheckCircle2, ArrowRight, Plus, Minus, Microscope, ShieldCheck, CreditCard, Heart, Users, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
const MAPS_URL = 'https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-2241">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-2242" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-2243">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-2244">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-2245">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2246">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-2247" /> : <Plus className="w-4 h-4" data-id="element-2248" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-2249">
        <p className="text-gray-600 leading-relaxed" data-id="element-2250">{answer}</p>
      </div>
    </div>;
}
export function RussianDentistPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const services = [{
    id: 'fillings',
    title: 'Пломбирование зубов',
    desc: 'Эстетичные фотополимерные пломбы, которые выглядят естественно.',
    icon: Microscope
  }, {
    id: 'root-canal',
    title: 'Лечение каналов',
    desc: 'Безболезненное лечение под микроскопом для сохранения зуба.',
    icon: Scan
  }, {
    id: 'crowns',
    title: 'Коронки и протезирование',
    desc: 'Керамические коронки и мосты для восстановления функции.',
    icon: ShieldCheck
  }, {
    id: 'hygiene',
    title: 'Профессиональная гигиена',
    desc: 'Чистка Airflow и ультразвук для здоровья десен.',
    icon: Star
  }, {
    id: 'whitening',
    title: 'Отбеливание зубов',
    desc: 'Безопасное осветление эмали на несколько тонов.',
    icon: Star
  }, {
    id: 'extraction',
    title: 'Удаление зубов',
    desc: 'Атравматичное удаление, включая зубы мудрости.',
    icon: ShieldCheck
  }, {
    id: 'smile-makeover',
    title: 'Smile Makeover',
    desc: 'Комплексная эстетическая трансформация улыбки.',
    icon: Star
  }, {
    id: 'cosmetic',
    title: 'Инъекционная косметология',
    desc: 'Эстетические процедуры для гармонии лица.',
    icon: Star
  }];
  const faqs = [{
    q: 'Есть ли русскоязычный стоматолог в Лондоне?',
    a: 'Да. В Dentico Clinic вы можете получить консультацию и лечение на русском языке в центре Лондона — район Angel / Islington (EC1V).'
  }, {
    q: 'Где находится Dentico Clinic?',
    a: 'Наш адрес: 360–364 City Road, London EC1V 2PY. Клиника расположена рядом с Angel Station (Islington).'
  }, {
    q: 'Как записаться на приём?',
    a: 'Вы можете записаться онлайн через сайт или связаться с нами через WhatsApp/Telegram. Также доступна запись по телефону: +44 7345 197820.'
  }, {
    q: 'Какие услуги предоставляет ваша стоматология?',
    a: 'Dentico Clinic предоставляет полный спектр стоматологических услуг: консультации, профессиональная гигиена, лечение кариеса, лечение каналов, коронки, виниры, имплантация и неотложная помощь.'
  }, {
    q: 'Принимаете ли вы пациентов из других районов Лондона?',
    a: "Да. К нам удобно добраться из Islington, King's Cross, Old Street, Shoreditch и Central London."
  }, {
    q: 'Можно ли получить консультацию на русском языке?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке во время консультации и лечения.'
  }, {
    q: 'Делаете ли вы срочные визиты при острой боли?',
    a: 'Да. Если у вас сильная боль, свяжитесь с нами как можно скорее — мы постараемся найти ближайшее доступное время.'
  }, {
    q: 'Что искать в Google, чтобы найти вашу клинику?',
    a: 'Попробуйте запросы: "русский стоматолог Лондон", "стоматология на русском Лондон", "Russian dentist London", "dentist near Angel station".'
  }];
  // FAQPage structured data for SEO
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <Layout data-id="element-2251">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-2252" />

      {/* FAQ Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-2253" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-2254">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-2255" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-2256">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2257">
            <div className="space-y-6" data-id="element-2258">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-2259">
                <span className="relative flex h-2 w-2" data-id="element-2260">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-2261"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-2262"></span>
                </span>
                Русскоязычный стоматолог в Лондоне
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-2263">
                Русскоязычный стоматолог в Лондоне –{' '}
                <span className="text-teal-600" data-id="element-2264">Dentico Clinic</span>
                <br data-id="element-2265" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-2266">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-2267">
                Современная цифровая стоматология в центре Лондона с
                консультацией на русском языке. Angel Station, EC1V 2PY.
              </p>

              {/* Trust Badges Row */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-2268">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2269">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-2270" />
                  5★ Google Reviews
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2271">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-2272" />
                  Central London, Angel
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2273">
                  <Scan className="w-4 h-4 text-teal-600" data-id="element-2274" />
                  Цифровая стоматология
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-2275">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-2276" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-2277">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2278">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-2279">
                    <Calendar className="w-5 h-5" data-id="element-2280" />
                    Записаться онлайн
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2281">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-2282">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-2283" />
                    WhatsApp
                  </Button>
                </a>
                <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2284">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-2285">
                    <TelegramIcon className="w-5 h-5 text-blue-500" data-id="element-2286" />
                    Telegram
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-2287">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-2288"></div>
              <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1200&q=80" alt="Русскоязычный стоматолог в Лондоне - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-2289" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHO WE ARE */}
      <section className="py-16 bg-white" data-id="element-2290">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2291">
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2292">
            Кто мы — Dentico Clinic
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-2293">
            Dentico Clinic — это современная частная стоматологическая клиника в
            районе Angel (Central London), где русскоязычные пациенты могут
            получить лечение на родном языке. Мы сочетаем британские стандарты
            качества, передовое цифровое оборудование и искреннюю заботу о
            каждом пациенте. Наши врачи зарегистрированы в GDC (General Dental
            Council) и имеют многолетний опыт работы в Великобритании.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 text-left" data-id="element-2294">
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-2295">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-2296">
                Современное оборудование
              </h3>
              <p className="text-sm text-gray-600" data-id="element-2297">
                Цифровые сканеры, микроскопы и 3D-диагностика для точного
                лечения.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-2298">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-2299">Опытные врачи</h3>
              <p className="text-sm text-gray-600" data-id="element-2300">
                Квалифицированные специалисты с опытом работы в частной практике
                Лондона.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-2301">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-2302">Прозрачные цены</h3>
              <p className="text-sm text-gray-600" data-id="element-2303">
                Честная стоимость лечения без скрытых платежей. Прайс доступен
                онлайн.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHY RUSSIANS CHOOSE US */}
      <section className="py-16 bg-gray-50" data-id="element-2304">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2305">
          <div className="text-center mb-12" data-id="element-2306">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2307">
              Почему русскоязычные пациенты выбирают Dentico Clinic
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2308">
              Мы понимаем важность общения на родном языке, особенно когда речь
              идет о здоровье.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2309">
            {[{
            icon: Languages,
            title: 'Русский язык',
            desc: 'Вам не нужно беспокоиться о языковом барьере. Мы объясним всё понятно.'
          }, {
            icon: Microscope,
            title: 'Современное оборудование',
            desc: 'Лечение под микроскопом и цифровые оттиски без дискомфорта.'
          }, {
            icon: CreditCard,
            title: 'Прозрачные цены',
            desc: 'Вы всегда знаете стоимость лечения до его начала.'
          }, {
            icon: MapPin,
            title: 'Удобное расположение',
            desc: 'Всего 5 минут от станции Angel (Northern Line), Central London.'
          }, {
            icon: Calendar,
            title: 'Онлайн-запись',
            desc: 'Удобная система бронирования 24/7 без звонков.'
          }, {
            icon: ShieldCheck,
            title: 'Опытные врачи',
            desc: 'Высокие стандарты стерилизации и безопасности (HTM 01-05).'
          }].map((item, idx) => <Card key={idx} className="p-6 hover:shadow-md transition-shadow" data-id="element-2310">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4" data-id="element-2311">
                  <item.icon className="w-6 h-6" data-id="element-2312" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2313">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-2314">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 3.5 COMMUNITY HUB SECTION */}
      <section className="py-16 bg-white border-t border-gray-100" data-id="element-2315">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2316">
          <div className="text-center mb-12" data-id="element-2317">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2318">
              Для русскоязычных в Лондоне
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2319">
              Мы понимаем, как важно иметь надёжного стоматолога, когда вы
              далеко от дома.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-2320">
            {[{
            icon: Heart,
            title: 'Мы понимаем ваши потребности',
            desc: 'Многие наши пациенты — русскоязычные, которые недавно переехали в Лондон. Мы знаем, с какими трудностями вы сталкиваетесь, и создаём комфортную атмосферу.'
          }, {
            icon: Languages,
            title: 'Объясняем лечение на русском',
            desc: 'Никакого языкового барьера. Мы подробно объясняем диагноз, план лечения и все варианты на понятном языке.'
          }, {
            icon: Users,
            title: 'Работаем с новоприбывшими',
            desc: 'Если вы недавно переехали в UK и не знаете, как работает стоматология здесь — мы поможем разобраться в системе и найти лучшее решение.'
          }, {
            icon: BookOpen,
            title: 'Разъясняем систему UK стоматологии',
            desc: 'NHS vs частная стоматология, как выбрать врача, что покрывает страховка — ответим на все ваши вопросы.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-teal-50 border-teal-100" data-id="element-2321">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-teal-600 mb-4 shadow-sm" data-id="element-2322">
                  <item.icon className="w-5 h-5" data-id="element-2323" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2324">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2325">
                  {item.desc}
                </p>
              </Card>)}
          </div>

          <div className="bg-teal-900 rounded-2xl p-8 text-center max-w-4xl mx-auto" data-id="element-2326">
            <p className="text-lg text-teal-50 font-medium leading-relaxed" data-id="element-2327">
              "Dentico Clinic — это не просто клиника, а место, где вас поймут.
              Мы гордимся тем, что стали надёжным стоматологическим партнёром
              для русскоязычного сообщества в Лондоне. Запишитесь на
              консультацию — и убедитесь сами."
            </p>
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-12 bg-teal-600" data-id="element-2328">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-2329">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-2330">
            Готовы записаться на приём?
          </h2>
          <p className="text-teal-100 mb-6" data-id="element-2331">
            Выберите удобный способ записи — онлайн, через мессенджер или
            закажите обратный звонок.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-2332">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2333">
              <Button size="lg" className="gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-2334">
                <Calendar className="w-5 h-5" data-id="element-2335" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-2336">
              <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" data-id="element-2337">
                <MessageCircle className="w-5 h-5" data-id="element-2338" />
                WhatsApp
              </Button>
            </a>
            <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" onClick={() => setIsCallbackOpen(true)} data-id="element-2339">
              <Phone className="w-5 h-5" data-id="element-2340" />
              Заказать звонок
            </Button>
          </div>
        </div>
      </section>

      {/* 4. SERVICES */}
      <section className="py-16 bg-gray-50" data-id="element-2341">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2342">
          <div className="text-center mb-12" data-id="element-2343">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2344">
              Наши стоматологические услуги в Лондоне
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-2345">
              Полный спектр современной стоматологии для всей семьи в Angel,
              Islington (EC1V).
            </p>
          </div>

          {/* Featured SEO service pages */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-2346">
            {[{
            to: '/ru/implantaciya-london',
            title: 'Имплантация зубов',
            desc: 'Восстановление утраченных зубов с помощью современных имплантов премиум-класса.',
            badge: 'Популярное'
          }, {
            to: '/ru/viniry-london',
            title: 'Виниры',
            desc: 'Керамические и композитные виниры для идеальной улыбки.',
            badge: 'Эстетика'
          }, {
            to: '/ru/lechenie-kanalov-london',
            title: 'Лечение каналов',
            desc: 'Безболезненная эндодонтия под микроскопом для сохранения зуба.',
            badge: 'Лечение'
          }, {
            to: '/ru/neotlozhnaya-stomatologiya-london',
            title: 'Неотложная помощь',
            desc: 'Срочный приём при острой боли, травме или отеке.',
            badge: 'Срочно'
          }].map((item, idx) => <Link key={idx} to={item.to} className="group block" data-id="element-2347">
                <Card className="h-full p-6 border-2 border-teal-100 hover:border-teal-400 hover:shadow-lg transition-all" data-id="element-2348">
                  <span className="inline-block text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full mb-3" data-id="element-2349">
                    {item.badge}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors" data-id="element-2350">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4" data-id="element-2351">{item.desc}</p>
                  <div className="flex items-center text-sm font-medium text-teal-600" data-id="element-2352">
                    Подробнее <ArrowRight className="w-4 h-4 ml-1" data-id="element-2353" />
                  </div>
                </Card>
              </Link>)}
          </div>

          {/* Other services grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2354">
            {services.map((service, idx) => <Link key={idx} to={`/ru/services/${service.id}`} className="group block" data-id="element-2355">
                <Card className="h-full p-6 hover:border-teal-200 hover:shadow-lg transition-all" data-id="element-2356">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 mb-4 group-hover:bg-teal-100 group-hover:text-teal-600 transition-colors" data-id="element-2357">
                    <service.icon className="w-5 h-5" data-id="element-2358" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors" data-id="element-2359">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4" data-id="element-2360">{service.desc}</p>
                  <div className="flex items-center text-sm font-medium text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2361">
                    Подробнее <ArrowRight className="w-4 h-4 ml-1" data-id="element-2362" />
                  </div>
                </Card>
              </Link>)}
          </div>
          <div className="text-center mt-10" data-id="element-2363">
            <Link to="/ru/services" data-id="element-2364">
              <Button variant="outline" size="lg" data-id="element-2365">
                Все услуги клиники
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. LOCATION */}
      <section className="py-16 bg-gray-50" data-id="element-2366">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2367">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100" data-id="element-2368">
            <div className="grid lg:grid-cols-2" data-id="element-2369">
              <div className="p-8 lg:p-12 flex flex-col justify-center" data-id="element-2370">
                <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2371">
                  Где находится клиника
                </h2>
                <div className="space-y-6" data-id="element-2372">
                  <div className="flex items-start gap-4" data-id="element-2373">
                    <div className="p-3 bg-teal-50 rounded-lg text-teal-600 shrink-0" data-id="element-2374">
                      <MapPin className="w-6 h-6" data-id="element-2375" />
                    </div>
                    <div data-id="element-2376">
                      <h3 className="font-bold text-gray-900 mb-1" data-id="element-2377">Адрес</h3>
                      <p className="text-gray-600" data-id="element-2378">360-364 City Road</p>
                      <p className="text-gray-600" data-id="element-2379">London EC1V 2PY</p>
                      <p className="text-sm text-teal-600 mt-2 font-medium" data-id="element-2380">
                        5 минут от Angel Station
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4" data-id="element-2381">
                    <div className="p-3 bg-teal-50 rounded-lg text-teal-600 shrink-0" data-id="element-2382">
                      <Calendar className="w-6 h-6" data-id="element-2383" />
                    </div>
                    <div data-id="element-2384">
                      <h3 className="font-bold text-gray-900 mb-1" data-id="element-2385">
                        Часы работы
                      </h3>
                      <p className="text-gray-600" data-id="element-2386">Пн - Сб: 10:00 - 18:00</p>
                      <p className="text-gray-600" data-id="element-2387">Вс: Закрыто</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3" data-id="element-2388">
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-id="element-2389">
                    <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2390">
                      <MapPin className="w-4 h-4" data-id="element-2391" />
                      Маршрут
                    </Button>
                  </a>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-2392">
                    <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto" data-id="element-2393">
                      <Calendar className="w-4 h-4" data-id="element-2394" />
                      Записаться
                    </Button>
                  </a>
                </div>
              </div>
              <div className="h-[400px] lg:h-auto bg-gray-200 relative" data-id="element-2395">
                <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" className="absolute inset-0" data-id="element-2396"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FAQ SECTION - SEO CRITICAL */}
      <section className="py-16 bg-white" data-id="element-2397">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2398">
          <div className="text-center mb-10" data-id="element-2399">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-2400">
              Частые вопросы
            </h2>
            <p className="text-gray-600" data-id="element-2401">
              Ответы на популярные запросы о русскоязычном стоматологе в
              Лондоне.
            </p>
          </div>

          <div className="space-y-2" data-id="element-2402">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-2403" />)}
          </div>
        </div>
      </section>

      {/* 7. BOOKING CTA */}
      <section className="py-16 bg-teal-900" data-id="element-2404">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-2405">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2406">
            Запишитесь на приём сегодня
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-2407">
            Позвоните, напишите или запишитесь онлайн — мы ответим на русском.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2408">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2409">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-2410">
                <Calendar className="w-5 h-5" data-id="element-2411" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2412">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-2413">
                <MessageCircle className="w-5 h-5" data-id="element-2414" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:w-auto" data-id="element-2415">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" onClick={() => setIsCallbackOpen(true)} data-id="element-2416">
                <Phone className="w-5 h-5" data-id="element-2417" />
                Заказать звонок
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}